import React, { useEffect, useState, useCallback } from "react";
import { Typography, useTheme, useMediaQuery } from "@mui/material";
import {
  DataGrid,
  GridToolbar,
  getGridStringOperators,
} from "@mui/x-data-grid";
import { Link } from "react-router-dom";

import Header from "../../../Header1";
import { tokens } from "../../../../theme";
import * as adminUserAction from "../../../../actions/admin/user";
import { getProfileImageUrl } from "../../../../utils/serverUtils";

const Contacts = () => {
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [rowCount, setRowCount] = useState(0);
  const [filterModel, setFilterModel] = useState({ items: [] });

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery("(max-width:768px)");

  const containsOperator = getGridStringOperators().find(
    (op) => op.value === "contains"
  );

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);

      const queryParams = new URLSearchParams({
        pageNo: page + 1,
        limit: pageSize,
      });

      filterModel.items.forEach((filter) => {
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ""
        ) {
          queryParams.append(`filter[${filter.columnField}]`, filter.value);
        }
      });

      const response = await adminUserAction.getApprovalLists(queryParams);

      const formattedData = response.results.map((item) => ({
        id: item.user_id,
        name: `${item.first_name} ${item.last_name || ""}`,
        age: new Date().getFullYear() - new Date(item.birthday).getFullYear(),
        phone: item.phone || "N/A",
        email: item.email || "N/A",
        country: item.country || "N/A",
        status: item.status,
        gender: item.gender === 1 ? "Men" : "Women",
        created_at: new Date(item.created_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        updated_at: new Date(item.updated_at).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        birthday: new Date(item.birthday).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        avatar: {
          hash: item.hash,
          type: item.type,
          extension: item.extension,
        },
      }));

      setCustomers(formattedData);
      setRowCount(response.total);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filterModel]);
  

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const desktopColumns = [
    {
      field: "avatar",
      headerName: "Avatar",
      flex: 1,
      filterable: false,
      renderCell: (params) => (
        <div className="avatar avatar-xs rounded-circle text-white border border-1 border-solid border-card">
          <img
            width="35"
            height="35"
            src={getProfileImageUrl(params.value)}
            className="rounded-circle"
            alt="avatar"
          />
        </div>
      ),
    },
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      filterOperators: [containsOperator],
    },
    {
      field: "age",
      headerName: "Age",
      type: "number",
      headerAlign: "left",
      align: "left",
      filterable: false,
    },
    {
      field: "phone",
      headerName: "Phone Number",
      flex: 1,
      filterOperators: [containsOperator],
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 1,
      filterOperators: [containsOperator],
      renderCell: (params) => <Typography>{params.value}</Typography>,
    },
    {
      field: "birthday",
      headerName: "Birthday",
      flex: 1,
      filterable: false,
    },
    {
      field: "gender",
      headerName: "Gender",
      flex: 1,
      filterable: false,
    },
    {
      field: "country",
      headerName: "Locations",
      flex: 1,
      filterable: false,
    },
    {
      field: "created_at",
      headerName: "Created At",
      flex: 1,
      filterable: false,
    },
    {
      field: "updated_at",
      headerName: "Updated At",
      flex: 1,
      filterable: false,
    },
    {
      field: "status",
      headerName: "Link",
      flex: 1,
      filterable: false,
      renderCell: (params) => (
        <Link
          to={`/admin/user/approval/${params.row.id}`}
          style={{ color: colors.blueAccent[400], textDecoration: "none" }}
        >
          Show
        </Link>
      ),
    },
  ];

  const mobileColumns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "phone", headerName: "Phone", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "status",
      headerName: "Link",
      flex: 1,
      renderCell: (params) => (
        <Link
          to={`/admin/user/detail/${params.row.id}`}
          className="text-primary text-decoration-none"
        >
          Show
        </Link>
      ),
    },
  ];

  return (
    <div className="container mt-4">
      <Header
        mb={2}
        title="Pending Approval List"
        subtitle="List of Pending Approval"
      />
      <div className="datagrid-wrapper mt-5">
        <DataGrid
          page={page}
          loading={loading}
          pageSize={pageSize}
          rows={customers}
          rowCount={rowCount}
          autoHeight
          pagination
          paginationMode="server"
          rowsPerPageOptions={[25, 50, 100]}
          components={{ Toolbar: GridToolbar }}
          columns={isMobile ? mobileColumns : desktopColumns}
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          filterModel={filterModel}
          onFilterModelChange={(newModel) => {
            const updatedModel = {
              ...newModel,
              items: newModel.items.map((item) => ({
                ...item,
                operatorValue: "contains", // force contains
              })),
            };
            setFilterModel(updatedModel);
            setPage(0);
          }}
          getRowId={(row) => row.id}
          componentsProps={{
            pagination: {
              sx: {
                "& .MuiButtonBase-root": { color: "#fff" },
                "& .MuiPaginationItem-root": { color: "#fff" },
              },
            },
          }}
        />
      </div>
    </div>
  );
};

export default Contacts;
